/* Update cart items */
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.cartDrawer = document.querySelector('cart-drawer');
      this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

      this.isCartPage = !!document.body.classList.contains('template-cart');

      this.options = {
        showCartUpsell: false,
      };

      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // Listen on `this` so the listener works even when children aren't available at construct time
      // (e.g. when this element is created via innerHTML replacement during cart drawer re-render)
      this.debouncedOnChange = debounce(event => {
        if (event.target != document.getElementById('Cart-note')) this.onChange(event);
      }, 300);

      this.addEventListener('change', this.debouncedOnChange.bind(this));
    }

    get sectionsToFetch() {
      const sections = [
        ...this.getCartSectionsToRender().map(section => section.section),
      ];
      if (this.isCartPage && this.options.showCartUpsell) {
        sections.push(...this.getCartRelatedSection().map(section => section.section));
      }
      else if (this.cartDrawer?.options.showCartDrawerUpsell) {
        sections.push(...this.getDrawerSectionsToRender().map(section => section.section));
      }
      return [...new Set(sections)];
    }

    onChange(event) {
      const lineItemEl = event.target.closest('[data-cart-item]');
      this.updateQuantity(
        lineItemEl.dataset.cartItemKey,
        event.target.value,
        document.activeElement.getAttribute('name'),
        lineItemEl,
      );
    }

    async updateQuantity(key, quantity, name, el, renderAllSections = false) {
      this.enableLoading(key);

      const updates = {};
      // check if the item is a repeat product
      const hasRepeat = el.dataset.cartItemHasRepeat;

      if (hasRepeat) {
        const repeatAboEl = this.querySelector(`[data-cart-item-is-repeat-for="${hasRepeat}"]`).dataset.cartItemKey;
        // remove the item from the cart
        updates[key] = quantity;
        updates[repeatAboEl] = quantity;
      }
      else {
        updates[key] = quantity;
      }

      const body = JSON.stringify({
        updates,
        sections: this.sectionsToFetch,
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          throw new Error(response.status);
        }

        this.parsedState = await response.json();

        window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: this.parsedState }));

        // On success:
        if (+quantity === 0) {
          // create a new element with the undo text
          const textWrapper = document.createElement('p');
          if (window.cartStrings.reAddItem) {
            textWrapper.classList.add('cart-drawer-is-open:text-sm');
            textWrapper.innerHTML = window.cartStrings.removedItem.replace('[titel]', el.dataset.cartItemName).replace('[link]', '#');

            // append the element
            el.appendChild(textWrapper);
          }

          const elementsToUpdate = [el];
          if (hasRepeat) {
            elementsToUpdate.push(this.querySelector(`[data-cart-item-is-repeat-for="${hasRepeat}"]`));
          }

          // animate the item to be removed
          elementsToUpdate.forEach(el => {
            el.querySelector('[data-cart-item-wrapper]').style.gridTemplateRows = '0fr';
            el.querySelector('[data-cart-item-wrapper]').addEventListener('transitionend', event => {
              // When the customer has removed an item, show what was removed with the option to add it again.
              if (window.cartStrings.reAddItem) {
                // remove the line item element
                event.target.remove();

                // add event listener
                const reAddLink = textWrapper.querySelector('a');
                reAddLink.addEventListener('click', event => {
                  event.preventDefault();
                  this.reAddOrUpdateItem(key, name, el);
                });

                setTimeout(() => {
                  el.closest('li').remove();
                }, 5000);
              }
              else {
                el.closest('li').remove();
              }
            });
          });
        }

        // always render the sections
        this.updateContent(this.parsedState, el, name, renderAllSections);

        if (this.parsedState.item_count !== 0) {
          if (hasRepeat) {
            this.querySelector(`[data-cart-item-is-repeat-for="${hasRepeat}"]`).querySelector('[data-repeat-quantity]').textContent = `x ${quantity}`;
          }
        }
        else if (window.cartStrings.reAddItem) {
          // hide the cart CTAs and totals
          document.querySelector('.js-cart-footer-contents').innerHTML = '';
          setTimeout(() => {
            this.cartDrawer?.querySelector('#cartDrawer').classList.toggle('cart-is-empty', this.parsedState.item_count === 0);
            this.classList.toggle('cart-is-empty', this.parsedState.item_count === 0);
          }, 5000);
        }
        else {
          this.classList.toggle('cart-is-empty', this.parsedState.item_count === 0);
          this.cartDrawer?.querySelector('#cartDrawer').classList.toggle('cart-is-empty', this.parsedState.item_count === 0);

          // hide the upsell
          document.getElementById('cartRelated')?.classList.add('hidden');
        }
      }
      catch (error) {
        debug() && console.error('Error updating the item.', error);

        document.querySelector('[data-cart-loader-active="show"]')?.classList.add('hidden');
        document.querySelector('[data-cart-loader-active="hidden"]')?.classList.remove('hidden');
        // document.getElementById('cart-errors').textContent = window.cartStrings.error;

        // Do a new fetch to the cart so you get the correct values of the item that had errors
        if (error.toString().includes('422')) {
          this.updateAfterError(key);
        }
      }
      finally {
        this.disableLoading();
      }
    }

    async updateAfterError(key) {
      const id = key.split(':')[0];
      if (!id) return;

      // Do a new fetch to the cart so you get the correct values of the item that had errors
      const body = JSON.stringify({
        sections: this.sectionsToFetch,
        sections_url: window.location.pathname,
      });
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig('javascript'),
        ...{ body },
      });

      const parsedState = await response.json();
      const lineItemWithError = parsedState.items.filter(item => item.id == id)[0];

      // show the error
      if (lineItemWithError) this.showLineItemError(id, lineItemWithError.quantity);
    }

    async updateContent(parsedState, el, name, renderAllSections = false) {

      if (!parsedState.sections) {
        const sectionIds = this.getCartSectionsToRender(renderAllSections).map(section => section.section);
        const sectionsResponse = await fetch(`/?sections=${sectionIds.join(',')}`);
        parsedState.sections = await sectionsResponse.json();
      }

      let sectionsToRender = [
        ...this.getCartSectionsToRender(renderAllSections),
      ];
      if (this.isCartPage && this.options.showCartUpsell) {
        sectionsToRender = [
          ...sectionsToRender,
          ...this.getCartRelatedSection(),
        ];
      }
      else if (this.cartDrawer && this.cartDrawer.options.showCartDrawerUpsell) {
        sectionsToRender = [
          ...sectionsToRender,
          ...this.getDrawerSectionsToRender(),
        ];
      }

      sectionsToRender.forEach((section, index) => {
        if (section?.selector) {
          const selector = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

          if (selector && section.selector && parsedState.sections?.[section.section]) {
            selector.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          }
          if (section.selector === '.js-cart-related-items-swiper') {
            // init the swiper after a change
            setTimeout(() => {
              selector.querySelector('swiper-slider').init();
            }, 500);
          }

          if (index === sectionsToRender.length - 1) {
            setTimeout(() => {
              window.initCartCheckoutButtons();
              window.initDialogs(document.querySelector('cart-drawer'));
            }, 200);
          }
        }
      });

      if (el && name) el.querySelector(`[name="${name}"]`)?.focus();
    }

    // show error text for the line item
    showLineItemError(id, quantity) {
      document.getElementById(`Line-item-error-${id}`).querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace('[quantity]', quantity);
      document.getElementById(`Quantity-${id}`).value = quantity;

      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    async reAddOrUpdateItem(key, name, el, quantity = 1, isRepeat = false, title) {
      const id = !!key.includes(':') ? key.split(':')[0] : key;

      // check if item is already in the cart, if so, run the updateQuantity function
      const itemInCart = this.querySelector(`[data-cart-item-id="${id}"]`);
      if (itemInCart) {
        const quantity = +itemInCart.querySelector('[data-qty-input]').value + 1;
        this.updateQuantity(itemInCart.dataset.cartItemKey, quantity, name, itemInCart, true);
      }
      else {
        const items = [
          {
            id,
            quantity,
          },
        ];

        // repeat abo product
        if (isRepeat) {
          items[0].properties = {
            '_hasRepeat': isRepeat.toString(),
            '_repeatProduct': id,
          };
          items.push({
            id: window.repeatProduct.repeat,
            quantity,
            selling_plan: window.repeatProduct.repeat_selling_plan,
            parent_id: id,
            properties: {
              '_repeatProduct': id,
              '_repeatProductTitle': title,
            },
          });
        }
        const body = JSON.stringify({
          ...{ items },
          sections: this.sectionsToFetch,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(routes.cart_add_url, { ...fetchConfig(),
            ...{ body } });

          if (!response.ok) {
            throw new Error(response.status);
          }
          const parsedState = await response.json();

          // render the sections
          this.updateContent(parsedState, el, name, true);
        }
        catch (error) {
          debug() && console.error('Error re-adding the item.', error);
        }
      }
    }

    async swapVariant(key, swapId, qty, el, name, title) {
      this.enableLoading(key);

      // First, remove the current item
      let body = JSON.stringify({
        id: key,
        quantity: 0,
      });

      try {
        // Remove the current item
        const removeResponse = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!removeResponse.ok) {
          throw new Error(removeResponse.status);
        }
        else {
          this.reAddOrUpdateItem(swapId, name, el, +qty, true, title);
        }
      }
      catch (error) {
        debug() && console.error('Error removing the item variant.', error);
      }
      finally {
        this.disableLoading();
      }
    }

    getCartSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '.js-cart-item-contents',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '.js-cart-footer-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'theme-cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];
    }

    getDrawerSectionsToRender() {
      return [
        {
          id: 'cartDrawer',
          section: document.getElementById('cartDrawer').dataset.id,
          selector: '.js-cart-related-items-swiper',
        },
      ];
    }

    getCartRelatedSection() {
      return [
        {
          id: 'cartRelated',
          section: document.getElementById('cartRelated').dataset.id,
          selector: '.cart-related-items',
        },
      ];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const htmlSelector = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (htmlSelector) {
        return htmlSelector.innerHTML;
      }
    }

    enableLoading() {
      document.querySelector('cart-items').classList.add('cart-items-is-loading');
      document.querySelector('[data-cart-loader-active="show"]')?.classList.remove('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList.add('hidden');
      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    disableLoading() {
      document.querySelector('cart-items').classList.remove('cart-items-is-loading');
    }
  }
  customElements.define('cart-items', CartItems);
}

/* Update the cart note */
if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', debounce(event => {
        const body = JSON.stringify( {
          note: event.target.value,
        });
        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{
            body,
          },
        });
      }, 300));
    }
  }
  customElements.define('cart-note', CartNote);
}

/* Swap cart item variant */
if (!customElements.get('cart-item-swap')) {
  class CartItemSwap extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener('click', event => {
        event.preventDefault();
        const lineItemEl = event.target.closest('[data-cart-item]');
        const { cartItemSwapQty, cartItemSwapId } = event.currentTarget.dataset;

        this.closest('cart-items').swapVariant(
          lineItemEl.dataset.cartItemKey,
          cartItemSwapId,
          cartItemSwapQty,
          lineItemEl,
          lineItemEl.dataset.cartItemName,
          lineItemEl.dataset.cartItemProductTitle,
        );
      });
    }
  }
  customElements.define('cart-item-swap', CartItemSwap, { extends: 'button' });
}

window.initCartCheckoutButtons = function() {
  const subscriptionButtonsShopify = document.querySelectorAll('[data-fh-hide-if-no-selling-plan]');
  const buttonWrappers = document.querySelectorAll('[data-checkout-buttons]');

  subscriptionButtonsShopify.forEach(button => {
    const icons = button.querySelector('span');
    const text = button.innerText.trim();

    if (text != '' && buttonWrappers.length > 0) {
      buttonWrappers.forEach(button => {
        button.querySelector('[data-selling-plan-text]').innerHTML = text;
        button.querySelector('[data-selling-plan-text]').classList.remove('hidden');
      });
    }

    if (icons) {
      button.innerHTML = icons.innerHTML;
    }
    button.classList.add('button--subscription');
    button.classList.remove('button--lg');
  });
};

// Cart checkout buttons for subscriptions
document.addEventListener('DOMContentLoaded', () => {
  window.initCartCheckoutButtons();
});